/**
 * Decides whether an ending session goes to the tenant's SAML entry point instead of `/signin`
 * (PLA-10003). Shared with PROD-1875's login-side redirect: one record, one 60s stamp and one
 * in-flight lock, so the two surfaces cannot loop together. `withSuppression()` (`sso=off`) tells
 * the sign-in page not to redirect again.
 */
import { API_ROOT, FEATURES } from 'appConstants';

export interface SsoConfig {
  authMethod?: 'sso' | 'password';
  // Fingerprint of the bearer token that signed in through SSO; only that session can redirect.
  sessionKey?: string;
  tenantSchema?: string;
  logoutRedirectEnabled?: boolean;
  lastRedirectAt?: number;
}

export type SsoConfigUpdate = Omit<SsoConfig, 'lastRedirectAt'>;

export interface SsoRedirectState {
  global?: {
    authUser?: { bearer_token?: string };
    tenantConfig?: {
      name?: string;
      features?: Partial<Record<string, boolean>>;
    };
  };
}

export type SignOutRedirectDecision =
  | { url: string }
  | { reason: 'capped' }
  | { reason: 'ineligible' };

// TODO(PROD-1875): parameter spelling is assumed (AF-4); ratify with PROD-1875's owner.
export const SUPPRESS_SSO_PARAM = 'sso';
export const SUPPRESS_SSO_VALUE = 'off';

const STORAGE_KEY = 'ssoConfig';
const AUTOMATIC_REDIRECT_CAP_MS = 60 * 1000;

let redirectInFlight = false;

const getStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const parseSsoConfig = (raw: string | null): SsoConfig => {
  if (!raw) {
    return {};
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {};
  }
  const record = parsed as Record<string, unknown>;
  const config: SsoConfig = {};
  if (record.authMethod === 'sso' || record.authMethod === 'password') {
    config.authMethod = record.authMethod;
  }
  if (typeof record.sessionKey === 'string') {
    config.sessionKey = record.sessionKey;
  }
  if (typeof record.tenantSchema === 'string') {
    config.tenantSchema = record.tenantSchema;
  }
  if (typeof record.logoutRedirectEnabled === 'boolean') {
    config.logoutRedirectEnabled = record.logoutRedirectEnabled;
  }
  if (typeof record.lastRedirectAt === 'number') {
    config.lastRedirectAt = record.lastRedirectAt;
  }
  return config;
};

const writeSsoConfig = (config: SsoConfig) => {
  try {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Storage full or blocked: the redirect degrades to today's /signin behaviour.
  }
};

export const readSsoConfig = (): SsoConfig => {
  try {
    return parseSsoConfig(getStorage()?.getItem(STORAGE_KEY) ?? null);
  } catch {
    return {};
  }
};

// FNV-1a: an equality check, not a secret (the token itself is in localStorage authUser).
export const ssoSessionKey = (token: string | undefined): string | undefined => {
  if (!token) {
    return undefined;
  }
  let hash = 0x811c9dc5;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
};

export const saveSsoConfig = (partial: SsoConfigUpdate): void => {
  const current = readSsoConfig();
  // A tenant switch (change_application) keeps the session and its token, so the session fields
  // survive it; willRedirectOnSignOut still requires sessionKey to match the live token.
  const tenantChanged =
    partial.tenantSchema !== undefined &&
    current.tenantSchema !== undefined &&
    partial.tenantSchema !== current.tenantSchema;
  const base: SsoConfig = tenantChanged
    ? { authMethod: current.authMethod, sessionKey: current.sessionKey }
    : current;
  writeSsoConfig({ ...base, ...partial });
};

// Keeps the stamp: every automatic redirect follows a sign-out, so clearing it would defeat the cap.
export const clearSsoConfig = (): void => {
  const config = readSsoConfig();
  delete config.authMethod;
  delete config.sessionKey;
  writeSsoConfig(config);
};

// Exported for PROD-1875's login-side redirect, so both surfaces share one cap and one URL.
export const isAutomaticRedirectCapped = (): boolean => {
  const { lastRedirectAt } = readSsoConfig();
  if (lastRedirectAt === undefined) {
    return false;
  }
  const elapsed = Date.now() - lastRedirectAt;
  return elapsed >= 0 && elapsed < AUTOMATIC_REDIRECT_CAP_MS;
};

export const idpEntryUrl = (schema: string): string =>
  `${API_ROOT}/v4/dash/saml2/new/${encodeURIComponent(schema)}`;

export const willRedirectOnSignOut = (
  state: SsoRedirectState | undefined,
  { explicit = false }: { explicit?: boolean } = {}
): SignOutRedirectDecision => {
  const record = readSsoConfig();
  // The record is shared by every tab: another tab's SSO sign-in must not qualify this session.
  if (
    record.authMethod !== 'sso' ||
    !record.sessionKey ||
    record.sessionKey !== ssoSessionKey(state?.global?.authUser?.bearer_token)
  ) {
    return { reason: 'ineligible' };
  }

  // NOTE: reads state directly, not via makeSelectTenantConfig: containers/App/selectors imports
  // hooks, and pulling that into utils/request risks an import cycle.
  const tenantConfig = state?.global?.tenantConfig;
  const liveSchema = tenantConfig?.name;
  let schema: string | undefined;
  let enabled: boolean;
  if (liveSchema) {
    if (record.tenantSchema !== undefined && record.tenantSchema !== liveSchema) {
      return { reason: 'ineligible' };
    }
    schema = liveSchema;
    enabled = tenantConfig?.features?.[FEATURES.REDIRECT_TO_IDP_ON_LOGOUT.key] === true;
  } else {
    schema = record.tenantSchema;
    enabled = record.logoutRedirectEnabled === true;
  }

  if (!schema || !enabled) {
    return { reason: 'ineligible' };
  }
  if (!explicit && isAutomaticRedirectCapped()) {
    return { reason: 'capped' };
  }
  return { url: idpEntryUrl(schema) };
};

// Lock, stamp and navigation stay in one synchronous call: the saga's takeLatest can cancel a
// sign-out at any yield, and a stamp written without its navigation would cap the successor.
export const redirectToIdp = (url: string): void => {
  if (redirectInFlight) {
    return;
  }
  redirectInFlight = true;
  // Back from the IdP can restore this page from bfcache with the lock still held, which would
  // leave RestrictedRoute loading forever and swallow every later sign-out.
  window.addEventListener(
    'pageshow',
    event => {
      if (event.persisted) {
        redirectInFlight = false;
      }
    },
    { once: true }
  );
  writeSsoConfig({ ...readSsoConfig(), lastRedirectAt: Date.now() });
  window.location.assign(url);
};

export const isRedirectInFlight = (): boolean => redirectInFlight;

export const withSuppression = (path: string): string =>
  `${path}${path.includes('?') ? '&' : '?'}${SUPPRESS_SSO_PARAM}=${SUPPRESS_SSO_VALUE}`;
